// src/pages/AdminUsersPage.tsx
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { authApi } from "../lib/auth-api";
import type { AdminUserActionRequest, AdminUserRow } from "@shared/auth";

export function AdminUsersPage() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: authApi.adminUsers,
  });
  const m = useMutation({
    mutationFn: authApi.adminUserAction,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  if (isLoading) return <PageWrap><div className="text-slate-400">読み込み中…</div></PageWrap>;
  if (error)     return <PageWrap><div className="auth-error">{(error as Error).message}</div></PageWrap>;
  if (!data)     return null;

  const pending = data.rows.filter((r) => r.pending_reason === "approve_pending");
  const others = data.rows.filter((r) => r.pending_reason !== "approve_pending");

  return (
    <PageWrap>
      {pending.length > 0 && (
        <section className="mb-6">
          <h2 className="font-semibold text-amber-800 mb-2 text-sm">
            ⚠️ 承認待ち ({pending.length}) — 例外ドメインからの申請
          </h2>
          <UserTable rows={pending} onAction={(req) => m.mutate(req)} pendingMode />
        </section>
      )}
      <section>
        <h2 className="font-semibold text-slate-800 mb-2 text-sm">
          全ユーザ ({data.rows.length})
        </h2>
        <UserTable rows={others} onAction={(req) => m.mutate(req)} />
      </section>
      {m.error && <div className="auth-error mt-3">{(m.error as Error).message}</div>}
      <div className="mt-6 text-xs">
        <button onClick={() => refetch()} className="text-blue-700 hover:underline">再読み込み</button>
      </div>
    </PageWrap>
  );
}

function PageWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
          <h1 className="text-base font-semibold">ユーザ管理</h1>
          <div className="text-xs space-x-3">
            <Link to="/" className="text-blue-700 hover:underline">ダッシュボードへ</Link>
            <Link to="/admin/events" className="text-blue-700 hover:underline">監査ログ</Link>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 py-4">{children}</main>
    </div>
  );
}

function UserTable({
  rows, onAction, pendingMode = false,
}: {
  rows: AdminUserRow[];
  onAction: (req: AdminUserActionRequest) => void;
  pendingMode?: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-100 text-left">
            <th className="px-2 py-1 border border-slate-200">ID</th>
            <th className="px-2 py-1 border border-slate-200">Email</th>
            <th className="px-2 py-1 border border-slate-200">状態</th>
            <th className="px-2 py-1 border border-slate-200">責任者</th>
            <th className="px-2 py-1 border border-slate-200">開発版</th>
            <th className="px-2 py-1 border border-slate-200">経路</th>
            <th className="px-2 py-1 border border-slate-200">登録日時</th>
            <th className="px-2 py-1 border border-slate-200">最終ログイン</th>
            <th className="px-2 py-1 border border-slate-200">操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className={u.disabled ? "text-slate-400" : ""}>
              <td className="px-2 py-1 border border-slate-200">{u.id}</td>
              <td className="px-2 py-1 border border-slate-200">{u.email}</td>
              <td className="px-2 py-1 border border-slate-200">
                {u.disabled ? <span className="text-red-700">無効</span>
                  : u.is_admin ? <span className="text-purple-700">admin</span>
                  : !u.verified ? <span className="text-orange-700">未確認</span>
                  : !u.approved ? <span className="text-amber-700">承認待ち</span>
                  : <span className="text-green-700">有効</span>}
              </td>
              <td className="px-2 py-1 border border-slate-200">
                {u.is_responsible
                  ? <span className="text-blue-700 font-semibold">責任者</span>
                  : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-2 py-1 border border-slate-200">
                {u.is_admin
                  ? <span className="text-slate-400 text-[11px]">(admin)</span>
                  : u.beta
                  ? <span className="text-amber-700 font-semibold">🧪 許可</span>
                  : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-2 py-1 border border-slate-200">
                {u.via_whitelist ? "ホワイトリスト" : "例外"}
              </td>
              <td className="px-2 py-1 border border-slate-200">{fmtDt(u.created_at)}</td>
              <td className="px-2 py-1 border border-slate-200">{fmtDt(u.last_login_at)}</td>
              <td className="px-2 py-1 border border-slate-200 space-x-1 whitespace-nowrap">
                {pendingMode && (
                  <>
                    <Btn onClick={() => onAction({ user_id: u.id, action: "approve" })} color="green">承認</Btn>
                    <Btn onClick={() => onAction({ user_id: u.id, action: "reject"  })} color="red">拒否</Btn>
                  </>
                )}
                {!pendingMode && !u.disabled && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "disable" })} color="red">無効化</Btn>
                )}
                {u.disabled && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "enable"  })} color="green">有効化</Btn>
                )}
                {!u.is_admin && u.approved && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "make_admin" })} color="purple">admin化</Btn>
                )}
                {u.is_admin && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "revoke_admin" })} color="slate">admin解除</Btn>
                )}
                {/* 責任者 role 切替 (= 主治医関連 UI 閲覧可)。
                    admin と独立軸。 disabled / pendingMode では出さない。 */}
                {!pendingMode && u.approved && !u.disabled && !u.is_responsible && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "make_responsible" })} color="blue">責任者化</Btn>
                )}
                {!pendingMode && u.is_responsible && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "revoke_responsible" })} color="slate">責任者解除</Btn>
                )}
                {/* 開発版 (/beta = リリース前タブ / /theta = 新GP定義) 利用許可。admin は常に閲覧可なので toggle 不要。
                    ★DB 列は互換のため users.beta のまま (2026-08-24 に呼称のみ変更)。
                    approved かつ disabled でない非adminにのみ表示。 */}
                {!pendingMode && !u.is_admin && u.approved && !u.disabled && !u.beta && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "grant_beta" })} color="amber">開発版許可</Btn>
                )}
                {!pendingMode && !u.is_admin && u.beta && (
                  <Btn onClick={() => onAction({ user_id: u.id, action: "revoke_beta" })} color="slate">開発版解除</Btn>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="px-2 py-3 text-center text-slate-400 border border-slate-200">
              該当ユーザなし
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Btn({ onClick, color, children }: {
  onClick: () => void; color: "green" | "red" | "purple" | "slate" | "blue" | "amber"; children: React.ReactNode;
}) {
  const map = {
    green:  "bg-green-50  text-green-700  hover:bg-green-100  border-green-200",
    red:    "bg-red-50    text-red-700    hover:bg-red-100    border-red-200",
    purple: "bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200",
    slate:  "bg-slate-50  text-slate-700  hover:bg-slate-100  border-slate-200",
    blue:   "bg-blue-50   text-blue-700   hover:bg-blue-100   border-blue-200",
    amber:  "bg-amber-50  text-amber-700  hover:bg-amber-100  border-amber-200",
  };
  return (
    <button
      type="button" onClick={onClick}
      className={`px-2 py-0.5 text-xs border rounded ${map[color]}`}
    >
      {children}
    </button>
  );
}

function fmtDt(s: string | null): string {
  if (!s) return "-";
  // SQLite datetime() は "YYYY-MM-DD HH:MM:SS" (UTC)
  return s.slice(0, 16);
}
